import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  useMediaQuery,
} from '@mui/material';
import { AnimatePresence } from 'motion/react';
import {
  Navigate,
  Route,
  Routes,
  useLocation,
} from 'react-router-dom';
import './App.css';
import LoginPage from './components/auth/LoginPage';
import ProtectedRoute from './components/auth/ProtectedRoute';
import Sidebar from './components/Sidebar';
import Topbar from './components/Topbar';
import { transportNavigationItems } from './config/navigation';
import { normalizeSession } from './auth/session';
import {
  AdminHomePage,
  ApplicationRoutePage,
  OperationalPage,
  TransportDashboardPage,
} from './pages/LogisticsPages';
import { getCurrentUser } from './services';
import { puedeVerPanelAdmin } from './utils/permissions';

const applicationPages = transportNavigationItems;

const platformPages = [
  {
    label: 'Integraciones',
    path: '/admin/integraciones',
    eyebrow: 'Plataforma',
    description: 'Sincronizacion con Business Central y servicios de soporte.',
  },
  {
    label: 'Auditoria',
    path: '/admin/auditoria',
    eyebrow: 'Plataforma',
    description: 'Consulta de actividad registrada por la API.',
  },
];

function App() {
  const isMobile = useMediaQuery('(max-width:900px)');
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [session, setSession] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const location = useLocation();
  const canViewAdmin = puedeVerPanelAdmin(session);
  const pageTitle = getPageTitle(location.pathname);

  useEffect(() => {
    let mounted = true;

    Promise.resolve(getCurrentUser())
      .then((currentSession) => {
        if (!mounted) {
          return;
        }

        setSession(normalizeSession(currentSession));
      })
      .catch(() => {
        if (mounted) {
          setSession(null);
        }
      })
      .finally(() => {
        if (mounted) {
          setAuthChecked(true);
        }
      });

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    setSidebarOpen(!isMobile);
  }, [isMobile]);

  useEffect(() => {
    if (isMobile) {
      setSidebarOpen(false);
    }
  }, [isMobile, location.pathname]);

  const handleMenuClick = () => {
    if (isMobile) {
      setSidebarOpen((value) => !value);
      return;
    }

    setSidebarCollapsed((value) => !value);
  };

  const handleLoginSuccess = useCallback((currentSession) => {
    setSession(
      currentSession?.user ? normalizeSession(currentSession) : currentSession
    );
    setAuthChecked(true);
  }, []);

  const protectedElement = (element) => (
    <ProtectedRoute authChecked={authChecked} session={session}>
      {element}
    </ProtectedRoute>
  );

  if (location.pathname === '/login') {
    return (
      <Routes>
        <Route
          path="/login"
          element={
            session ? (
              <Navigate replace to="/" />
            ) : (
              <LoginPage onLogin={handleLoginSuccess} />
            )
          }
        />
        <Route path="*" element={<Navigate replace to="/login" />} />
      </Routes>
    );
  }

  return (
    <Box className="suite-shell">
      <Sidebar
        open={sidebarOpen}
        mobile={isMobile}
        collapsed={sidebarCollapsed}
        onClose={() => setSidebarOpen(false)}
        onToggleCollapse={() => setSidebarCollapsed((value) => !value)}
        session={session}
      />
      <Box className="suite-main">
        <Topbar
          mobile={isMobile}
          onMenuClick={handleMenuClick}
          sidebarCollapsed={sidebarCollapsed}
          sidebarOpen={sidebarOpen}
          session={session}
          title={pageTitle}
        />
        <Box component="main" className="suite-content">
          <AnimatePresence mode="wait" initial={false}>
            <Routes location={location} key={location.pathname}>
              <Route path="/" element={protectedElement(<TransportDashboardPage />)} />
              <Route
                path="/admin"
                element={
                  protectedElement(
                    canViewAdmin ? <AdminHomePage /> : <Navigate replace to="/" />
                  )
                }
              />
              {applicationPages
                .filter((page) => page.path !== '/')
                .map((page) => (
                  <Route
                    key={page.path}
                    path={page.path}
                    element={protectedElement(<ApplicationRoutePage page={page} />)}
                  />
                ))}
              {platformPages.map((page) => (
                <Route
                  key={page.path}
                  path={page.path}
                  element={
                    protectedElement(
                      canViewAdmin ? (
                        <OperationalPage page={page} />
                      ) : (
                        <Navigate replace to="/" />
                      )
                    )
                  }
                />
              ))}
              <Route path="*" element={<Navigate replace to="/" />} />
            </Routes>
          </AnimatePresence>
        </Box>
      </Box>
    </Box>
  );
}

function getPageTitle(pathname) {
  if (pathname === '/') {
    return 'Cargas';
  }

  if (pathname === '/admin') {
    return 'Panel administrador';
  }

  const page =
    applicationPages.find((item) => item.path === pathname) ||
    platformPages.find((item) => item.path === pathname);

  return page ? page.label : 'Clasol Suite Apps';
}

export default App;
